import { atom } from 'jotai';

import {
  createDerivedWritableAtom,
  createDerivedWritableAtomFamily,
  filterNonNullable,
  isFunction,
} from '../../../../../utils';
import { videoSDKAtom } from '../../../atoms';
import {
  LocalAudioContextAtom,
  LocalMediaStatus,
  LocalVideoContextAtom,
  RemoteParticipantWithMediaMetadata,
} from '../../../types';
import { participantsAtom, remoteAudioDelegatorsAtom } from '../atoms';

// Audio Delegator
export const localAudioContextAtom = createDerivedWritableAtom(videoSDKAtom, 'localAudioContext');

export const localAudioStatusAtom = createDerivedWritableAtom(localAudioContextAtom, 'status');

export const isOwnAudioOffAtom = atom((get) => {
  const { delegator, status } = get(localAudioContextAtom);
  return !delegator || status !== LocalMediaStatus.READY;
});

export const currentMicrophoneIdAtom = atom((get) => get(localAudioContextAtom).delegator?.deviceId);

export const allAudioTracksAtom = atom((get) => {
  const localAudioTrack = get(localAudioContextAtom);
  const remoteAudioTracks = get(remoteAudioDelegatorsAtom);

  return filterNonNullable([localAudioTrack.delegator, ...remoteAudioTracks]);
});

// Video Delegator
export const localVideoContextAtom = createDerivedWritableAtom(videoSDKAtom, 'localVideoContext');

export const localVideoStatusAtom = createDerivedWritableAtom(localVideoContextAtom, 'status');

export const localMediaStatusesAtom = atom(
  (get) => ({
    audio: get(localAudioStatusAtom),
    video: get(localVideoStatusAtom),
  }),
  (
    get,
    set,
    updater:
      | {
          audio: LocalAudioContextAtom['status'];
          video: LocalVideoContextAtom['status'];
        }
      | ((prev: { audio: LocalAudioContextAtom['status']; video: LocalVideoContextAtom['status'] }) => {
          audio: LocalAudioContextAtom['status'];
          video: LocalVideoContextAtom['status'];
        }),
  ) => {
    const updatedValues = isFunction(updater) ? updater(get(localMediaStatusesAtom)) : updater;
    set(localAudioStatusAtom, updatedValues.audio);
    set(localVideoStatusAtom, updatedValues.video);
  },
);

export const isOwnVideoOffAtom = atom((get) => {
  const { delegator, status } = get(localVideoContextAtom);
  return !delegator || status !== LocalMediaStatus.READY;
});

export const currentCameraIdAtom = atom((get) => get(localVideoContextAtom).delegator?.deviceId);

export const localScreenShareContextAtom = createDerivedWritableAtom(videoSDKAtom, 'localScreenShareContext');

export const isScreenSharingAtom = atom((get) => {
  const { delegator, status } = get(localScreenShareContextAtom);
  return !!delegator && status === LocalMediaStatus.READY;
});

// Remote Screen Share
export const remoteScreenShareParticipantAtom = createDerivedWritableAtom(videoSDKAtom, 'remoteScreenShareParticipant');

export const remoteScreenShareVideoDelegatorAtom = createDerivedWritableAtom(
  videoSDKAtom,
  'remoteScreenShareVideoDelegator',
);

export const remoteScreenShareAudioDelegatorAtom = createDerivedWritableAtom(
  videoSDKAtom,
  'remoteScreenShareAudioDelegator',
);

export const isScreenShareModeAtom = atom((get) => {
  const isLocalSharing = get(isScreenSharingAtom);
  const remoteScreenShareVideoDelegator = get(remoteScreenShareVideoDelegatorAtom);
  return isLocalSharing || !!remoteScreenShareVideoDelegator;
});

export const isShowingScreenShareConfirmationAtom = createDerivedWritableAtom(
  videoSDKAtom,
  'isShowingScreenShareConfirmation',
);

export const remoteMediaMetadataByParticipantSidAtom = createDerivedWritableAtom(
  videoSDKAtom,
  'remoteMediaMetadataByParticipantSid',
);

export const remoteVideoMetadataAtomFamily = createDerivedWritableAtomFamily(
  remoteMediaMetadataByParticipantSidAtom,
  'video',
);

export const remoteParticipantsWithMediaMetadataAtom = atom<RemoteParticipantWithMediaMetadata[]>((get) => {
  const remoteParticipants = get(participantsAtom);
  const remoteMediaMetadataByParticipantSid = get(remoteMediaMetadataByParticipantSidAtom);

  return remoteParticipants.map((participant) => {
    const mediaMetadata = remoteMediaMetadataByParticipantSid[participant.sid];
    return {
      participant,
      mediaMetadata,
    };
  });
});
